import { Avatar, Box } from '@mui/material';
import Paper from '@mui/material/Paper';
import SurveyEmail from 'client/modules/Emails/Survey/SurveyEmail';
import AddResponsePage from 'client/modules/Response/pages/AddResponsePages/AddResponsePage';
import styles from 'client/modules/Surveys/pages/CustomizeSurvey/CustomizeEmail.css';
import { surveyMessages } from 'client/modules/Surveys/SurveyReducer';
import MailWindow from 'components/MailWindow/MailWindow';
import PropTypes from 'prop-types';
import React, { useRef, useMemo, useState, useEffect } from 'react';
import Frame, { FrameContextConsumer } from 'react-frame-component';
import { renderEmail } from 'react-html-email';
import { FormattedMessage, IntlProvider, useIntl } from 'react-intl';
import { localizationData } from '../../../../../Intl/setup';

function MailPreview(props) {
  const { formatMessage } = useIntl();
  const iframeDocument = useRef();
  const iframe = useRef();

  const resizeIframe = () => {
    setTimeout(() => {
      if (iframeDocument.current) {
        const { offsetHeight } = iframeDocument.current.documentElement;
        if (offsetHeight) {
          iframe.current.style.height = `${offsetHeight + 20}px`;
        }
      }
    }, 100);
  };

  // State to hold the current email HTML (starts with current props)
  const [emailHtml, setEmailHtml] = useState('');

  // Create a stable reference for the current props
  const currentProps = useMemo(
    () => ({
      locale: props.locale,
      surveyType: props.surveyType,
      surveyCsatType: props.surveyCsatType,
      csatFacesNum: props.csatFacesNum,
      brand: props.brand,
      topColor: props.topColor,
      buttonColor: props.buttonColor,
      buttonStyle: props.buttonStyle,
      buttonShape: props.buttonShape,
      question: props.question,
      intro: props.intro,
      footer: props.footer,
      surveyLanguage: props.surveyLanguage,
      logoUrl: props.logoUrl,
      fromWithDefault: props.fromWithDefault,
      replyTo: props.replyTo,
      subjectWithDefault: props.subjectWithDefault,
      rand: props.rand,
    }),
    [
      props.locale,
      props.surveyType,
      props.surveyCsatType,
      props.csatFacesNum,
      props.brand,
      props.topColor,
      props.buttonColor,
      props.buttonStyle,
      props.buttonShape,
      props.question,
      props.intro,
      props.footer,
      props.surveyLanguage,
      props.logoUrl,
      props.fromWithDefault,
      props.replyTo,
      props.subjectWithDefault,
      props.rand,
    ],
  );

  // Debounced effect to update email HTML asynchronously
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      // Perform the expensive renderEmail operation asynchronously
      const newEmailHtml = renderEmail(
        <IntlProvider
          locale={currentProps.locale}
          messages={localizationData[currentProps.locale].messages}
        >
          <SurveyEmail
            onClick={() => {}} // Use empty function to avoid dependency issues
            preview={props.preview}
            surveyType={currentProps.surveyType}
            surveyCsatType={currentProps.surveyCsatType}
            csatFacesNum={currentProps.csatFacesNum}
            brand={currentProps.brand}
            topColor={currentProps.topColor}
            buttonColor={currentProps.buttonColor}
            buttonStyle={currentProps.buttonStyle}
            buttonShape={currentProps.buttonShape}
            from={currentProps.fromWithDefault}
            replyTo={currentProps.replyTo}
            question={currentProps.question}
            intro={currentProps.intro}
            footer={currentProps.footer}
            subject={currentProps.subjectWithDefault}
            language={currentProps.surveyLanguage || currentProps.locale}
            logoUrl={currentProps.logoUrl ? `${currentProps.logoUrl}?r=${currentProps.rand}` : null}
          />
        </IntlProvider>,
      );

      setEmailHtml(newEmailHtml);
    }, 30); // 30ms debounce delay

    return () => {
      clearTimeout(timeoutId);
    };
  }, [currentProps, props.preview]);

  // Initialize with first render
  useEffect(() => {
    if (!emailHtml) {
      const initialEmailHtml = renderEmail(
        <IntlProvider
          locale={currentProps.locale}
          messages={localizationData[currentProps.locale].messages}
        >
          <SurveyEmail
            onClick={() => {}}
            preview={props.preview}
            surveyType={currentProps.surveyType}
            surveyCsatType={currentProps.surveyCsatType}
            csatFacesNum={currentProps.csatFacesNum}
            brand={currentProps.brand}
            topColor={currentProps.topColor}
            buttonColor={currentProps.buttonColor}
            buttonStyle={currentProps.buttonStyle}
            buttonShape={currentProps.buttonShape}
            from={currentProps.fromWithDefault}
            replyTo={currentProps.replyTo}
            question={currentProps.question}
            intro={currentProps.intro}
            footer={currentProps.footer}
            subject={currentProps.subjectWithDefault}
            language={currentProps.surveyLanguage || currentProps.locale}
            logoUrl={currentProps.logoUrl ? `${currentProps.logoUrl}?r=${currentProps.rand}` : null}
          />
        </IntlProvider>,
      );
      setEmailHtml(initialEmailHtml);
    }
  }, [
    currentProps.brand,
    currentProps.buttonColor,
    currentProps.buttonShape,
    currentProps.buttonStyle,
    currentProps.csatFacesNum,
    currentProps.footer,
    currentProps.fromWithDefault,
    currentProps.intro,
    currentProps.locale,
    currentProps.logoUrl,
    currentProps.question,
    currentProps.rand,
    currentProps.replyTo,
    currentProps.subjectWithDefault,
    currentProps.surveyCsatType,
    currentProps.surveyLanguage,
    currentProps.surveyType,
    currentProps.topColor,
    emailHtml,
    props.preview,
  ]); // Only run once on mount

  return (
    <Paper className={styles.preview}>
      <div className={styles.previewCover}>
        <FormattedMessage
          id="Survey.ThisIsAPreview"
          defaultMessage="This is a preview of the email we will send to your customers."
        />
      </div>
      {props.score === null ? (
        <MailWindow>
          <div className={styles.header}>
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
              <Box>
                <Avatar
                  sx={{
                    fontSize: 16,
                    fontWeight: 400,
                    width: 30,
                    height: 30,
                    background:
                      'linear-gradient(225deg, rgba(160,166,182,1) 0%, rgba(144,146,158,1) 100%)',
                  }}
                >
                  {props.fromWithDefault
                    .split(' ')
                    .map(([i]) => i?.toUpperCase())
                    .slice(0, 2)
                    .join('')}
                </Avatar>
              </Box>
              <Box>
                <Box className={styles.from}>
                  <strong>{props.fromWithDefault}</strong>
                </Box>
                <Box>{props.subjectWithDefault}</Box>
                <Box>
                  {formatMessage(surveyMessages['Survey.Reply'])}
                  <span className={styles.dim}>{props.replyTo}</span>
                </Box>
              </Box>
            </Box>
          </div>
          <Frame
            ref={iframe}
            className={styles.frame}
            contentDidMount={resizeIframe}
            contentDidUpdate={resizeIframe}
          >
            <FrameContextConsumer>
              {({ document }) => {
                iframeDocument.current = document;
              }}
            </FrameContextConsumer>
            <div
              role="none"
              onClick={props.onClick}
              style={{
                zoom: 0.82,
                transition: 'opacity 0.2s ease',
              }}
              dangerouslySetInnerHTML={{
                __html: emailHtml,
              }}
            />
          </Frame>
        </MailWindow>
      ) : (
        <div style={{ zoom: 0.82 }}>
          <AddResponsePage
            language={props.surveyLanguage || props.locale}
            score={props.score}
            onSubmit={props.onSubmit}
            followUpQuestion={props.followUpQuestion}
            brand={props.brand}
            logoUrl={props.logoUrl}
            topColor={props.topColor}
            buttonColor={props.buttonColor}
            buttonStyle={props.buttonStyle}
            buttonShape={props.buttonShape}
            drivers={props.drivers}
            followupStrategy={props.followupStrategy}
          />
        </div>
      )}
    </Paper>
  );
}

MailPreview.propTypes = {
  fromWithDefault: PropTypes.string,
  subjectWithDefault: PropTypes.string,
  replyTo: PropTypes.any,
  preview: PropTypes.bool,
  score: PropTypes.any,
  onClick: PropTypes.func,
  locale: PropTypes.any,
  surveyType: PropTypes.string,
  surveyCsatType: PropTypes.string,
  csatFacesNum: PropTypes.number,
  brand: PropTypes.any,
  topColor: PropTypes.any,
  buttonColor: PropTypes.any,
  buttonStyle: PropTypes.string,
  buttonShape: PropTypes.string,
  question: PropTypes.string,
  intro: PropTypes.string,
  footer: PropTypes.string,
  surveyLanguage: PropTypes.any,
  logoUrl: PropTypes.any,
  rand: PropTypes.string,
  onSubmit: PropTypes.func,
  followUpQuestion: PropTypes.string,
  fullWidth: PropTypes.bool,
  drivers: PropTypes.any,
  followupStrategy: PropTypes.string,
};

export default MailPreview;
